"use client"

import type React from "react"
import "../../../styles/admin-buttons.css"
import { useEffect, useState } from "react"
import { Button, Form, Modal } from "react-bootstrap"
import { useAppDispatch, useAppSelector } from "../../../redux/hooks"
import { fetchArticles, createArticle, updateArticle, deleteArticle } from "../../../redux/slices/blogSlice"
import { useTranslation } from "../../../hooks/use-translation"
import { Search, Edit, Trash2, Upload } from "lucide-react"
import { useToast } from "../../../components/ui/use-toast"
import { formatDate } from "../../../utils/timestamp"
import { PlusButton } from "../../../components/ui/plus-button"

export default function AdminBlogPage() {
  const dispatch = useAppDispatch()
  const { articles = [], isLoading } = useAppSelector((state) => state.blog)
  const { t } = useTranslation()
  const { toast } = useToast()
  const [searchTerm, setSearchTerm] = useState("")
  const [showModal, setShowModal] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [selectedArticle, setSelectedArticle] = useState<any>(null)
  const [formData, setFormData] = useState({
    title: "",
    preview: "",
    content: "",
  })
  const [thumbnail, setThumbnail] = useState<File | null>(null)
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; articleId: string; articleTitle: string }>({
    show: false,
    articleId: "",
    articleTitle: "",
  })

  useEffect(() => {
    dispatch(fetchArticles())
  }, [dispatch])

  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      setThumbnail(file)
      setThumbnailPreview(URL.createObjectURL(file))
    }
  }

  const handleCreateArticle = async () => {
    if (!formData.title || !formData.preview || !formData.content) {
      toast({
        title: "Error",
        description: "Please fill in all fields",
        variant: "destructive",
      })
      return
    }

    try {
      const result = await dispatch(
        createArticle({
          ...formData,
          thumbnail: thumbnail || undefined,
        }),
      ).unwrap()

      toast({
        title: "Success",
        description: "Article created successfully",
      })
      setShowModal(false)
      resetForm()
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create article",
        variant: "destructive",
      })
    }
  }

  const handleUpdateArticle = async () => {
    if (!formData.title || !formData.preview || !formData.content) {
      toast({
        title: "Error",
        description: "Please fill in all fields",
        variant: "destructive",
      })
      return
    }

    try {
      const result = await dispatch(
        updateArticle({
          id: selectedArticle.id,
          ...formData,
          thumbnail: thumbnail || undefined,
        }),
      ).unwrap()

      toast({
        title: "Success",
        description: "Article updated successfully",
      })
      setShowModal(false)
      resetForm()
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update article",
        variant: "destructive",
      })
    }
  }

  const handleDeleteArticle = async (id: string, title: string) => {
    setDeleteConfirm({
      show: true,
      articleId: id,
      articleTitle: title,
    })
  }

  const confirmDeleteArticle = async () => {
    try {
      const result = await dispatch(deleteArticle(deleteConfirm.articleId)).unwrap()
      toast({
        title: "Success",
        description: "Article deleted successfully",
      })
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete article",
        variant: "destructive",
      })
    } finally {
      setDeleteConfirm({ show: false, articleId: "", articleTitle: "" })
    }
  }

  const handleEditArticle = (article: any) => {
    setSelectedArticle(article)
    setFormData({
      title: article.title,
      preview: article.preview,
      content: article.content,
    })
    setThumbnailPreview(article.thumbnail || null)
    setThumbnail(null)
    setIsEditing(true)
    setShowModal(true)
  }

  const resetForm = () => {
    setFormData({
      title: "",
      preview: "",
      content: "",
    })
    setThumbnail(null)
    setThumbnailPreview(null)
    setIsEditing(false)
    setSelectedArticle(null)
  }

  const filteredArticles = articles
    ? articles.filter(
        (article) =>
          article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          article.preview.toLowerCase().includes(searchTerm.toLowerCase()) ||
          article.content.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : []

  return (
    <div className="container-fluid py-4 px-2 px-md-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="fs-2 fw-bold mb-0 text-white">{t("blogManagement")}</h1>
        <PlusButton
          onClick={() => {
            resetForm()
            setShowModal(true)
          }}
          title={t("newArticle")}
        />
      </div>

      <div className="card mb-4 border-0 shadow-sm">
        <div className="card-body p-4">
          <div className="position-relative mb-3">
            <input
              type="text"
              placeholder={t("searchArticles")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-control ps-5"
            />
            <Search className="position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" size={18} />
          </div>

          {isLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th className="fw-semibold text-gray-700">{t("title")}</th>
                    <th className="fw-semibold text-gray-700">{t("preview")}</th>
                    <th className="fw-semibold text-gray-700">{t("date")}</th>
                    <th className="fw-semibold text-gray-700">{t("actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredArticles.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-4 text-muted">
                        {t("noArticles")}
                      </td>
                    </tr>
                  ) : (
                    filteredArticles.map((article) => (
                      <tr key={article.id} className="border-bottom">
                        <td className="align-middle">{article.title}</td>
                        <td className="align-middle">
                          <div className="text-truncate" style={{ maxWidth: "300px" }}>
                            {article.preview}
                          </div>
                        </td>
                        <td className="align-middle">{formatDate(article.date)}</td>
                        <td className="align-middle">
                          <div className="d-flex gap-2">
                            <Button
                              className="admin-edit-btn"
                              size="sm"
                              onClick={() => handleEditArticle(article)}
                              title={t("editArticle")}
                            >
                              <Edit size={16} />
                            </Button>
                            <Button
                              className="admin-delete-btn"
                              size="sm"
                              onClick={() => handleDeleteArticle(article.id, article.title)}
                              title={t("deleteArticle")}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Article Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? t("editArticle") : t("newArticle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{t("title")}</Form.Label>
              <Form.Control
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder={t("enterTitle")}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("preview")}</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.preview}
                onChange={(e) => setFormData({ ...formData, preview: e.target.value })}
                placeholder={t("enterPreview")}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("content")}</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder={t("enterContent")}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("thumbnail")}</Form.Label>
              <div className="d-flex flex-column gap-2">
                {thumbnailPreview && (
                  <div className="mb-2">
                    <img
                      src={thumbnailPreview || "/placeholder.svg"}
                      alt="Thumbnail Preview"
                      className="img-fluid rounded"
                      style={{ maxHeight: "200px" }}
                    />
                  </div>
                )}
                <div className="d-flex align-items-center gap-2">
                  <Button
                    variant="outline-secondary"
                    onClick={() => document.getElementById("thumbnail")?.click()}
                    className="d-flex align-items-center"
                  >
                    <Upload className="size-4 me-2" />
                    {t("selectImage")}
                  </Button>
                  <Form.Control
                    id="thumbnail"
                    type="file"
                    accept="image/*"
                    className="d-none"
                    onChange={handleThumbnailChange}
                  />
                  {thumbnail && <span className="small text-muted">{thumbnail.name}</span>}
                </div>
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <button className="modal-cancel-btn" onClick={() => setShowModal(false)}>
            {t("cancel")}
          </button>
          <button
            className="modal-primary-btn"
            onClick={() => (isEditing ? handleUpdateArticle() : handleCreateArticle())}
          >
            {isEditing ? t("update") : t("create")}
          </button>
        </Modal.Footer>
      </Modal>
      {/* Delete Confirmation Modal */}
      <Modal
        show={deleteConfirm.show}
        onHide={() => setDeleteConfirm({ show: false, articleId: "", articleTitle: "" })}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("confirmDelete")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t("deleteArticleConfirmation", { title: deleteConfirm.articleTitle })}</p>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="modal-cancel-btn"
            onClick={() => setDeleteConfirm({ show: false, articleId: "", articleTitle: "" })}
          >
            {t("cancel")}
          </button>
          <button className="modal-delete-btn" onClick={confirmDeleteArticle}>
            {t("delete")}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
